import { log } from "@soulbyte/core";
import { z } from "zod";

const fronteggRoleSchema = z
  .object({
    id: z.string(),
    key: z.string().nullish(),
    name: z.string(),
    level: z.union([z.number(), z.string()]).nullish(),
    isDefault: z.union([z.boolean(), z.string()]).nullish(),
    description: z.string().nullish(),
  })
  .passthrough();

export type FronteggIdentityRole = {
  id: string;
  key?: string;
  name: string;
  level?: number;
  isDefault?: boolean;
  description?: string;
};

const fronteggUserTenantSchema = z
  .object({
    roles: z.array(fronteggRoleSchema).optional(),
    tenantId: z.string(),
    isDisabled: z.boolean().optional(),
  })
  .passthrough();

const fronteggUserRowSchema = z
  .object({
    id: z.string(),
    name: z.string().optional(),
    email: z.string(),
    roles: z.array(fronteggRoleSchema).optional(),
    tenants: z.array(fronteggUserTenantSchema).optional(),
    isLocked: z.boolean().optional(),
    createdAt: z.string().optional(),
    lastLogin: z.string().optional(),
    profilePictureUrl: z.string().optional(),
    activatedForTenant: z.boolean().optional(),
  })
  .passthrough();

const fronteggUsersMetadataSchema = z
  .object({
    totalItems: z.number().optional(),
    totalPages: z.number().optional(),
  })
  .passthrough();

// rows stay unknown here and get parsed one by one, so a single odd user
// (missing email, say) drops that row instead of the whole page
const fronteggUsersPayloadSchema = z
  .object({
    _metadata: fronteggUsersMetadataSchema.optional(),
    items: z.array(z.unknown()),
  })
  .passthrough();

export type FronteggUsersPage = {
  limit: number;
  offset: number;
  totalItems: number;
  totalPages: number;
  users: FronteggIdentityUser[];
};

export const AUTH_USERS_PAGE_SIZE = 10;
export const AUTH_USERS_PAGINATION_THRESHOLD = 3;

export type FronteggIdentityUser = {
  id: string;
  name: string;
  email: string;
  roles: FronteggIdentityRole[];
  isDisabled: boolean;
  lastLogin?: string;
  profilePictureUrl?: string;
};

/**
 * Maps a Frontegg users row onto the tenant-scoped shape the Users page uses.
 * Prefers tenant association roles (v3), then top-level roles (v2).
 */
export function mapFronteggUserRow(
  row: z.infer<typeof fronteggUserRowSchema>,
  tenantId?: string,
): FronteggIdentityUser {
  const tenant =
    row.tenants?.find((item) => item.tenantId === tenantId) ??
    row.tenants?.[0];
  const isDisabled =
    tenant?.isDisabled === true || row.activatedForTenant === false;
  const rawRoles = tenant?.roles ?? row.roles ?? [];

  return {
    id: row.id,
    name: row.name?.trim() ? row.name : row.email,
    email: row.email,
    roles: rawRoles.map(function normalizeEmbeddedRole(role) {
      const level =
        typeof role.level === "string"
          ? Number(role.level)
          : (role.level ?? undefined);
      const isDefault =
        typeof role.isDefault === "string"
          ? role.isDefault === "true"
          : (role.isDefault ?? undefined);
      const normalized: FronteggIdentityRole = {
        id: role.id,
        name: role.name,
      };
      if (role.key) {
        normalized.key = role.key;
      }
      if (role.description) {
        normalized.description = role.description;
      }
      if (isDefault !== undefined) {
        normalized.isDefault = isDefault;
      }
      if (level !== undefined && Number.isFinite(level)) {
        normalized.level = level;
      }
      return normalized;
    }),
    isDisabled,
    lastLogin: row.lastLogin,
    profilePictureUrl: row.profilePictureUrl,
  };
}

export function getAuthUsersTotalPages(totalItems: number, limit: number) {
  if (totalItems <= 0) {
    return 0;
  }
  if (limit <= 0) {
    return 1;
  }
  return Math.ceil(totalItems / limit);
}

/**
 * Moves the signed-in user to the front of the current page when present.
 * Does not inject the user onto pages where they are absent.
 */
export function pinCurrentUserFirst<T extends { id: string }>(
  users: T[],
  currentUserId: string | undefined,
): T[] {
  if (!currentUserId || users.length === 0) {
    return users;
  }
  const index = users.findIndex((user) => user.id === currentUserId);
  if (index <= 0) {
    return users;
  }
  const pinned = users[index];
  if (!pinned) {
    return users;
  }
  return [pinned, ...users.slice(0, index), ...users.slice(index + 1)];
}

/** Inclusive row range for the current page (1-based start/end). */
export function getAuthUsersPageRange(
  limit: number,
  page: number,
  totalItems: number,
): { end: number; start: number } {
  if (totalItems <= 0) {
    return { end: 0, start: 0 };
  }
  const safeLimit = Math.max(1, limit);
  const start = (page - 1) * safeLimit + 1;
  const end = Math.min(page * safeLimit, totalItems);
  return { end, start };
}

export function parseFronteggUsersPagePayload(
  json: unknown,
  tenantId: string | undefined,
  limit: number,
  offset: number,
): FronteggUsersPage {
  const parsed = fronteggUsersPayloadSchema.safeParse(json);
  if (!parsed.success) {
    log("frontegg_users_payload_unreadable", {
      level: "warning",
      track: true,
      reason: "response did not match the users page shape",
      issues: parsed.error.issues.map((issue) => issue.path.join(".")),
    });
    return {
      limit,
      offset,
      totalItems: 0,
      totalPages: 0,
      users: [],
    };
  }
  const metadata = parsed.data._metadata;
  const users = parsed.data.items.flatMap(function keepReadableRows(item) {
    const row = fronteggUserRowSchema.safeParse(item);
    if (!row.success) {
      log("frontegg_user_row_skipped", {
        level: "warning",
        track: true,
        reason: "row did not match the user shape",
        issues: row.error.issues.map((issue) => issue.path.join(".")),
      });
      return [];
    }
    return [mapFronteggUserRow(row.data, tenantId)];
  });
  const totalItems = metadata?.totalItems ?? users.length;
  const totalPages =
    metadata?.totalPages ?? getAuthUsersTotalPages(totalItems, limit);

  return {
    limit,
    offset,
    totalItems,
    totalPages,
    users,
  };
}

export function parseFronteggUsersPayload(
  json: unknown,
  tenantId?: string,
): FronteggIdentityUser[] {
  return parseFronteggUsersPagePayload(json, tenantId, AUTH_USERS_PAGE_SIZE, 0)
    .users;
}

/** One row from GET …/users/v3/roles — role IDs only; names come from the roles catalog. */
const fronteggUserRolesAssignmentSchema = z
  .object({
    userId: z.string(),
    roleIds: z.array(z.string()),
    tenantId: z.string().optional(),
    vendorId: z.string().optional(),
  })
  .passthrough();

export type FronteggUserRoleAssignment = {
  userId: string;
  roleIds: string[];
};

/**
 * Parses GET …/users/v3/roles.
 * Accepts the typed array `{ userId, roleIds }[]`, or a userId→roleIds map (OpenAPI
 * models the response as an empty object, which is how the gateway often serializes it).
 */
export function parseFronteggUsersRolesPayload(
  json: unknown,
  tenantId?: string,
): FronteggUserRoleAssignment[] {
  if (Array.isArray(json)) {
    return json.flatMap((row) => {
      const parsed = fronteggUserRolesAssignmentSchema.safeParse(row);
      if (!parsed.success) {
        return [];
      }
      if (
        tenantId &&
        parsed.data.tenantId &&
        parsed.data.tenantId !== tenantId
      ) {
        return [];
      }
      return [{ userId: parsed.data.userId, roleIds: parsed.data.roleIds }];
    });
  }

  if (!json || typeof json !== "object") {
    return [];
  }

  const single = fronteggUserRolesAssignmentSchema.safeParse(json);
  if (single.success) {
    if (
      tenantId &&
      single.data.tenantId &&
      single.data.tenantId !== tenantId
    ) {
      return [];
    }
    return [{ userId: single.data.userId, roleIds: single.data.roleIds }];
  }

  const root = json as Record<string, unknown>;
  const nested = root.items ?? root.data;
  if (Array.isArray(nested)) {
    return parseFronteggUsersRolesPayload(nested, tenantId);
  }

  return Object.entries(root).flatMap(([userId, value]) => {
    if (Array.isArray(value) && value.every((item) => typeof item === "string")) {
      return [{ userId, roleIds: value }];
    }
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return [];
    }
    const parsed = fronteggUserRolesAssignmentSchema.safeParse({
      userId,
      ...value,
    });
    if (!parsed.success) {
      return [];
    }
    if (
      tenantId &&
      parsed.data.tenantId &&
      parsed.data.tenantId !== tenantId
    ) {
      return [];
    }
    return [{ userId: parsed.data.userId, roleIds: parsed.data.roleIds }];
  });
}

/** Resolves role IDs against the tenant roles catalog; unknown IDs keep the id as the name. */
export function resolveRolesFromCatalog(
  roleIds: string[],
  catalog: FronteggIdentityRole[],
): FronteggIdentityRole[] {
  const byId = new Map(catalog.map((role) => [role.id, role] as const));
  return roleIds.map((roleId) => {
    const fromCatalog =
      byId.get(roleId) ??
      catalog.find((role) => role.key === roleId || role.name === roleId);
    return fromCatalog ?? { id: roleId, name: roleId };
  });
}

/**
 * Overlays roles onto list rows. Prefer full role objects from a detail fetch;
 * otherwise join role ID assignments through the roles catalog.
 */
export function mergeUsersWithRoles(
  users: FronteggIdentityUser[],
  assignments: FronteggUserRoleAssignment[],
  catalog: FronteggIdentityRole[],
): FronteggIdentityUser[] {
  const rolesByUserId = new Map(
    assignments.map(
      (assignment) =>
        [
          assignment.userId,
          resolveRolesFromCatalog(assignment.roleIds, catalog),
        ] as const,
    ),
  );
  return users.map((user) => ({
    ...user,
    roles: rolesByUserId.get(user.id) ?? user.roles,
  }));
}

export function mergeUsersWithRoleDetails(
  users: FronteggIdentityUser[],
  detailedUsers: FronteggIdentityUser[],
): FronteggIdentityUser[] {
  const rolesByUserId = new Map(
    detailedUsers.map((user) => [user.id, user.roles] as const),
  );
  return users.map((user) => ({
    ...user,
    roles: rolesByUserId.get(user.id) ?? user.roles,
  }));
}
